// Libs
import React, {
  Dispatch,
  ElementRef,
  HTMLAttributes,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import Collapse from '@kunukn/react-collapse';
import classNames from 'classnames';

// Components
import CustomContextMenu from '@components/CustomContextMenu';
import { TabInfo } from '@components/TableWithTabs/types';
import HorizontalScrollMobile from '@presenters/web/components/HorizontalScrollMobile';

import MobileFilter from './mobileFilter';

/**
 * The `ResultsList` and `ResultsListItem` components can be used together to
 * display a results list.
 */
interface Props {
  listId?: string;
  children: React.ReactNode;
  summary?: React.ReactNode;
  filters?: React.ReactNode;
  filtersOrder?: string;
  className?: string;
  headerClassName?: string;
  darkBackground?: boolean;
  showFiltersInContextMenu?: boolean;
  contextMenuTitle?: string;
  areFiltersEmpty?: boolean;
  isHeaderPadded?: boolean;
  areTabsMultiple?: boolean;
  setActiveTab?: Dispatch<SetStateAction<TabInfo>>;
  scrollToTab?: number;
  hideMobileFilter?: boolean;
}

export const ResultsList: React.FC<Props> = ({
  listId,
  summary,
  children,
  darkBackground,
  filters,
  filtersOrder = '',
  className = '',
  headerClassName = '',
  isHeaderPadded = true,
  showFiltersInContextMenu,
  contextMenuTitle = '',
  areFiltersEmpty,
  areTabsMultiple,
  setActiveTab,
  scrollToTab,
  hideMobileFilter = false,
}) => {
  const customContextMenuRef = useRef<ElementRef<typeof CustomContextMenu>>(
    null
  );
  const [filtersAreOpen, setFiltersAreOpen] = useState(false);

  useEffect(() => {
    if (areFiltersEmpty && customContextMenuRef.current?.getMenuState()) {
      customContextMenuRef.current?.closeContextMenu();
    }
  }, [areFiltersEmpty]);

  const handleFilters = () => {
    if (showFiltersInContextMenu) {
      customContextMenuRef.current?.openContextMenu();
    } else {
      setFiltersAreOpen(state => !state);
    }
  };

  const summaryNode = areTabsMultiple ? (
    <HorizontalScrollMobile
      setActiveTab={setActiveTab}
      scrollToTab={scrollToTab}
    >
      {summary}
    </HorizontalScrollMobile>
  ) : (
    summary
  );

  return (
    <div
      data-label="List"
      data-cy="results-list"
      data-testid="search-results-list"
      className={className}
    >
      {(summary || filters) && (
        <>
          <div
            className={classNames(
              `flex items-center relative ${headerClassName}`,
              {
                'bg-gray-100 px-5': darkBackground,
                'justify-between': filters,
                'py-3': isHeaderPadded,
              }
            )}
          >
            {summaryNode}
            {filters && (
              <>
                <MobileFilter
                  filters={filters}
                  contextMenuTitle={contextMenuTitle}
                  handleFilters={handleFilters}
                  customContextMenuRef={customContextMenuRef}
                  showFiltersInContextMenu={showFiltersInContextMenu}
                  className={classNames(filtersOrder, {
                    hidden: areTabsMultiple || hideMobileFilter,
                  })}
                />
                {/* MRD-2185. The filters' bar is only visible on
                widths > 992px when there are no tabs. With tabs it shows
                when the screen is wider than 1200px. */}
                <div
                  className={classNames(
                    'hidden',
                    filtersOrder,
                    { 'desktop-m:flex': areTabsMultiple },
                    { 'desktop:flex': !areTabsMultiple },
                    'flex-grow justify-end'
                  )}
                >
                  {filters}
                </div>
              </>
            )}
          </div>
          {filters && areTabsMultiple && (
            <div className="desktop-m:hidden flex justify-items-end bg-gray-100 border-gray-300 px-8 border-t-3 justify-end py-3">
              {/* MRD-2185. For screen widths between 992px and 1200px the
              filters' bar moves here (where the button toggling the mobile
              filters would be) to let the tabs fill the upper row.  */}
              <div className="hidden desktop:flex flex-grow justify-end">
                {filters}
              </div>
              <MobileFilter
                filters={filters}
                contextMenuTitle={contextMenuTitle}
                handleFilters={handleFilters}
                customContextMenuRef={customContextMenuRef}
                showFiltersInContextMenu={showFiltersInContextMenu}
              />
            </div>
          )}
          {filters && !showFiltersInContextMenu && (
            <Collapse
              isOpen={filtersAreOpen}
              className="collapse-css-transition desktop:hidden"
              overflowOnExpanded
            >
              <div className={`px-5 ${darkBackground && 'bg-gray-100'}`}>
                {filters}
              </div>
            </Collapse>
          )}
        </>
      )}
      <ul className="border-t-3 border-gray-300" id={listId}>
        {children}
      </ul>
    </div>
  );
};

export const ResultsListItem: React.FC<{
  children: React.ReactNode;
  className?: string;
  columnLayout?: boolean;
  spacingY?: number;
  borderBottomClass?: string;
} & HTMLAttributes<HTMLLIElement>> = ({
  children,
  className = '',
  columnLayout,
  spacingY = 4,
  borderBottomClass = 'border-b',
  ...props
}) => (
  <li
    data-label="ListItem"
    className={`${!columnLayout &&
      'flex'} ${borderBottomClass} border-gray-300 py-${spacingY} ${className}`}
    {...props}
  >
    {children}
  </li>
);
